import { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { db } from './firebase';
import {
  collection, doc, setDoc, updateDoc, onSnapshot,
  query, where, getDocs, serverTimestamp, arrayUnion, deleteDoc
} from 'firebase/firestore';

function buildDeck() {
  const SUITS = ['♠', '♥', '♦', '♣'];
  const VALUES = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
  const POINTS = { A: 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10, J: 10, Q: 10, K: 0 };
  let d = [];
  for (let s of SUITS)
    for (let v of VALUES)
      d.push({ suit: s, value: v, points: POINTS[v], id: Math.random().toString(36).slice(2) });
  for (let i = d.length - 1; i > 0; i--) {
    let j = Math.floor(Math.random() * (i + 1));
    [d[i], d[j]] = [d[j], d[i]];
  }
  return d;
}

function generateCode() {
  return Math.random().toString(36).substring(2, 8).toUpperCase();
}

const S = {
  btn: { background: '#1D9E75', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 500, cursor: 'pointer' },
  btnOutline: { background: 'none', border: '1px solid #ddd', borderRadius: 8, padding: '10px 20px', fontSize: 14, cursor: 'pointer' },
  card: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: '1.5rem' },
  input: { width: '100%', fontSize: 14, padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8, boxSizing: 'border-box' },
};

export default function Lobby({ onGameStart, onBack }) {
  const { user, profile } = useAuth();
  const [view, setView] = useState('menu'); // menu | create | join | waiting | matchmaking
  const [gameId, setGameId] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [maxPlayers, setMaxPlayers] = useState(4);
  const [maxScore, setMaxScore] = useState(100);
  const [lobbyData, setLobbyData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Listen to lobby changes
  useEffect(() => {
    if (!gameId) return;
    const unsub = onSnapshot(doc(db, 'games', gameId), snap => {
      if (snap.exists()) {
        const data = snap.data();
        setLobbyData(data);
        if (data.status === 'playing') {
          onGameStart(gameId);
        }
      }
    });
    return () => unsub();
  }, [gameId]);

  const createGame = async () => {
    setLoading(true);
    setError('');
    try {
      const code = generateCode();
      const freshDeck = buildDeck();
      const newGameId = `game_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      const playerData = {
        uid: user.uid,
        name: profile?.displayName || user.displayName || 'Player',
        score: 0,
        cards: Array.from({ length: 4 }, (_, i) => ({ ...freshDeck.shift(), faceUp: false, position: i })),
        connected: true,
      };
      await setDoc(doc(db, 'games', newGameId), {
        code,
        hostId: user.uid,
        playerIds: [user.uid],
        players: [playerData],
        deck: freshDeck,
        discard: [],
        drawnCard: null,
        currentPlayer: 0,
        turnPhase: 'draw',
        round: 1,
        maxScore,
        maxPlayers,
        status: 'waiting',
        createdAt: serverTimestamp(),
        lastAction: serverTimestamp(),
        playerPresence: { [user.uid]: serverTimestamp() },
      });
      setGameId(newGameId);
      setView('waiting');
    } catch (e) {
      setError('Failed to create game. Please try again.');
    }
    setLoading(false);
  };

const joinGame = async () => {
    setLoading(true);
    setError('');
    try {
      const q = query(collection(db, 'games'), where('code', '==', joinCode.toUpperCase()));
      const snap = await getDocs(q);
      if (snap.empty) { setError('Game not found. Check your code and try again.'); setLoading(false); return; }
      const gameDoc = snap.docs[0];
      const data = gameDoc.data();
      if (data.status !== 'waiting') { setError('This game has already started.'); setLoading(false); return; }
      if (data.players.length >= data.maxPlayers) { setError('Game is full.'); setLoading(false); return; }
      if (data.playerIds.includes(user.uid)) {
        setGameId(gameDoc.id);
        setView('waiting');
        setLoading(false);
        return;
      }
      const freshDeck = [...data.deck];
      const playerData = {
        uid: user.uid,
        name: profile?.displayName || user.displayName || 'Player',
        score: 0,
        cards: Array.from({ length: 4 }, (_, i) => ({ ...freshDeck.shift(), faceUp: false, position: i })),
        connected: true,
      };
      await updateDoc(doc(db, 'games', gameDoc.id), {
        players: arrayUnion(playerData),
        playerIds: arrayUnion(user.uid),
        deck: freshDeck,
        [`playerPresence.${user.uid}`]: serverTimestamp(),
      });
      setGameId(gameDoc.id);
      setView('waiting');
    } catch (e) {
      setError('Failed to join game. Please try again.');
    }
    setLoading(false);
  };

  const startGame = async () => {
    if (!gameId) return;
    await updateDoc(doc(db, 'games', gameId), {
      status: 'playing',
      lastAction: serverTimestamp(),
    });
  };

  const findMatch = async () => {
    setLoading(true);
    setError('');
    try {
      // Look for open games with 1-3 players waiting
      const q = query(
        collection(db, 'games'),
        where('status', '==', 'waiting'),
        where('maxPlayers', '==', 4)
      );
      const snap = await getDocs(q);
      const available = snap.docs.filter(d => {
        const data = d.data();
        return data.players.length < data.maxPlayers && !data.playerIds.includes(user.uid);
      });

      if (available.length > 0) {
        // Join existing game
        const gameDoc = available[0];
        const data = gameDoc.data();
        const freshDeck = [...data.deck];
        const playerData = {
          uid: user.uid,
          name: profile?.displayName || user.displayName || 'Player',
          score: 0,
          cards: Array.from({ length: 4 }, (_, i) => ({ ...freshDeck.shift(), faceUp: false, position: i })),
          connected: true,
        };
        await updateDoc(doc(db, 'games', gameDoc.id), {
          players: arrayUnion(playerData),
          playerIds: arrayUnion(user.uid),
          deck: freshDeck,
          [`playerPresence.${user.uid}`]: serverTimestamp(),
        });
        setGameId(gameDoc.id);
        setView('waiting');
      } else {
        // Create new game and wait
        await createGame();
        setView('matchmaking');
      }
    } catch (e) {
      setError('Matchmaking failed. Please try again.');
    }
    setLoading(false);
  };

  const leaveGame = async () => {
    if (gameId && lobbyData?.hostId === user.uid) {
      await deleteDoc(doc(db, 'games', gameId));
    }
    setGameId('');
    setLobbyData(null);
    setView('menu');
  };

  // ── MENU ──────────────────────────────────────────────────────────────────
  if (view === 'menu') return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', fontFamily: 'sans-serif' }}>
      <div style={{ ...S.card, maxWidth: 400, width: '100%' }}>
        <div style={{ fontSize: 24, fontWeight: 500, marginBottom: 4 }}>🌐 Online Play</div>
        <div style={{ fontSize: 14, color: '#888', marginBottom: 24 }}>Play Four Play with friends or random opponents</div>
        <button onClick={() => setView('create')} style={{ ...S.btn, width: '100%', marginBottom: 10 }}>
          🎮 Create a game
        </button>
        <button onClick={() => setView('join')} style={{ ...S.btn, width: '100%', marginBottom: 10, background: '#1a2744' }}>
          🔑 Join with code
        </button>
        <button onClick={findMatch} style={{ ...S.btn, width: '100%', marginBottom: 10, background: '#7F77DD' }} disabled={loading}>
          {loading ? '⏳ Finding match...' : '🎲 Quick match'}
        </button>
        <button onClick={onBack} style={{ ...S.btnOutline, width: '100%' }}>Back</button>
        {error && <div style={{ color: '#E53E3E', fontSize: 13, marginTop: 10, textAlign: 'center' }}>{error}</div>}
      </div>
    </div>
  );

  // ── CREATE ────────────────────────────────────────────────────────────────
  if (view === 'create') return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', fontFamily: 'sans-serif' }}>
      <div style={{ ...S.card, maxWidth: 400, width: '100%' }}>
        <div style={{ fontSize: 20, fontWeight: 500, marginBottom: 20 }}>Create a game</div>
        <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>Max players</div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
          {[2,3,4,5,6,7,8].map(n => (
            <button key={n} onClick={() => setMaxPlayers(n)} style={{
              padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
              border: maxPlayers === n ? '2px solid #1D9E75' : '1px solid #ddd',
              background: maxPlayers === n ? '#E1F5EE' : '#fff',
              color: maxPlayers === n ? '#0F6E56' : '#333',
            }}>{n}</button>
          ))}
        </div>
        <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>Max score to end game</div>
        <select value={maxScore} onChange={e => setMaxScore(Number(e.target.value))}
          style={{ ...S.input, marginBottom: 20 }}>
          <option value={50}>50 points</option>
          <option value={100}>100 points</option>
          <option value={150}>150 points</option>
          <option value={200}>200 points</option>
        </select>
        <button onClick={createGame} style={{ ...S.btn, width: '100%', marginBottom: 10 }} disabled={loading}>
          {loading ? 'Creating...' : 'Create game'}
        </button>
        <button onClick={() => setView('menu')} style={{ ...S.btnOutline, width: '100%' }}>Back</button>
        {error && <div style={{ color: '#E53E3E', fontSize: 13, marginTop: 10 }}>{error}</div>}
      </div>
    </div>
  );

  // ── JOIN ──────────────────────────────────────────────────────────────────
  if (view === 'join') return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', fontFamily: 'sans-serif' }}>
      <div style={{ ...S.card, maxWidth: 400, width: '100%' }}>
        <div style={{ fontSize: 20, fontWeight: 500, marginBottom: 20 }}>Join a game</div>
        <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>Enter room code</div>
        <input
          value={joinCode}
          onChange={e => setJoinCode(e.target.value.toUpperCase())}
          placeholder="Enter 6-character code"
          maxLength={6}
          style={{ ...S.input, marginBottom: 20, fontSize: 20, textAlign: 'center', letterSpacing: 8, fontWeight: 500 }}
        />
        <button onClick={joinGame} style={{ ...S.btn, width: '100%', marginBottom: 10 }} disabled={loading || joinCode.length < 6}>
          {loading ? 'Joining...' : 'Join game'}
        </button>
        <button onClick={() => setView('menu')} style={{ ...S.btnOutline, width: '100%' }}>Back</button>
        {error && <div style={{ color: '#E53E3E', fontSize: 13, marginTop: 10 }}>{error}</div>}
      </div>
    </div>
  );

  // ── WAITING / MATCHMAKING ─────────────────────────────────────────────────
  if (view === 'waiting' || view === 'matchmaking') {
    const isHost = lobbyData?.hostId === user?.uid;
    const players = lobbyData?.players || [];
    const spots = (lobbyData?.maxPlayers || maxPlayers) - players.length;

    return (
      <div style={{ minHeight: '100vh', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', fontFamily: 'sans-serif' }}>
        <div style={{ ...S.card, maxWidth: 420, width: '100%' }}>
          <div style={{ fontSize: 20, fontWeight: 500, marginBottom: 4 }}>
            {view === 'matchmaking' ? '🎲 Finding players...' : '⏳ Waiting for players'}
          </div>
          <div style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>
            {view === 'matchmaking' ? 'Looking for opponents...' : `Share this code with friends:`}
          </div>

          {view === 'waiting' && lobbyData?.code && (
            <div style={{ background: '#f5f5f5', borderRadius: 8, padding: '1rem', textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Room code</div>
              <div style={{ fontSize: 36, fontWeight: 700, letterSpacing: 8, color: '#1D9E75' }}>{lobbyData.code}</div>
              <button onClick={() => navigator.clipboard?.writeText(lobbyData.code)} style={{ fontSize: 12, color: '#888', background: 'none', border: 'none', cursor: 'pointer', marginTop: 4 }}>
                📋 Copy code
              </button>
            </div>
          )}

          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>Players ({players.length}/{lobbyData?.maxPlayers || maxPlayers})</div>
            {players.map((p, i) => (
              <div key={p.uid} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#f9f9f9', borderRadius: 8, marginBottom: 6 }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#1D9E75', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 14, fontWeight: 500 }}>
                  {p.name.charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, fontSize: 14, fontWeight: 500 }}>{p.name}</div>
                {i === 0 && <span style={{ fontSize: 11, background: '#E1F5EE', color: '#0F6E56', padding: '2px 8px', borderRadius: 6 }}>Host</span>}
                {p.uid === user?.uid && <span style={{ fontSize: 11, color: '#888' }}>you</span>}
              </div>
            ))}
            {Array.from({ length: spots }, (_, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#f9f9f9', borderRadius: 8, marginBottom: 6, opacity: 0.5 }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#ddd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>?</div>
                <div style={{ fontSize: 14, color: '#aaa' }}>Waiting for player...</div>
              </div>
            ))}
          </div>

          {isHost && players.length >= 2 && (
            <button onClick={startGame} style={{ ...S.btn, width: '100%', marginBottom: 10 }}>
              Start game ({players.length} players)
            </button>
          )}
          {isHost && players.length < 2 && (
            <div style={{ textAlign: 'center', fontSize: 13, color: '#888', marginBottom: 10 }}>
              Need at least 2 players to start
            </div>
          )}
          {!isHost && (
            <div style={{ textAlign: 'center', fontSize: 13, color: '#888', marginBottom: 10 }}>
              ⏳ Waiting for host to start the game...
            </div>
          )}
          <button onClick={leaveGame} style={{ ...S.btnOutline, width: '100%' }}>Leave</button>
        </div>
      </div>
    );
  }

  return null;
}
